import React, { useState, useRef } from 'react';
import { useInView } from 'framer-motion';
import { motion } from '../../lib/motionShim';
import { PartyPopper } from 'lucide-react';
import { submitWaitlistEmail } from '../../lib/waitlist';
import StoreBadges from '../StoreBadges';

const WaitlistCTA = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setErrorMessage('Enter a valid email address.');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage('');
      await submitWaitlistEmail(email, 'landing-cta');
      setIsSubmitted(true);
      setEmail('');
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="waitlist"
      ref={sectionRef}
      className="section-padding bg-ston-bg relative overflow-hidden"
      data-testid="waitlist-section"
    >
      {/* Animated Mesh Gradient Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Orb 1 — Deep Purple, top-left */}
        <motion.div
          className="absolute w-[500px] h-[500px] md:w-[700px] md:h-[700px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(147, 51, 234, 0.15) 0%, transparent 70%)',
            top: '-20%',
            left: '-10%',
            }}
          animate={{
            x: [0, 60, 0],
            y: [0, 40, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
        {/* Orb 2 — Violet, center-right */}
        <motion.div
          className="absolute w-[400px] h-[400px] md:w-[600px] md:h-[600px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.12) 0%, transparent 70%)',
            top: '10%',
            right: '-15%',
            }}
          animate={{
            x: [0, -40, 0],
             y: [0, -30, 0],
            scale: [1.1, 1, 1.1],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
        {/* Orb 3 — Fuchsia, bottom-center */}
        <motion.div
          className="absolute w-[500px] h-[500px] md:w-[600px] md:h-[600px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(217, 70, 239, 0.1) 0%, transparent 70%)',
            bottom: '-25%',
            left: '30%',
            }}
          animate={{
            x: [0, -50, 0],
            y: [0, -20, 0],
            scale: [1, 1.15, 1],
          }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
        {/* Subtle grid overlay */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.015]">
          <defs>
            <pattern id="waitlistGrid" width="50" height="50" patternUnits="userSpaceOnUse">
              <path d="M 50 0 L 0 0 0 50" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-ston-purple" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#waitlistGrid)" />
        </svg>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-center"
        >
          <span className="section-label">GET THE APP</span>

          <h2
            className="mt-4 font-outfit font-bold text-ston-text leading-tight"
            style={{
              fontSize: 'clamp(36px, 5vw, 56px)',
              letterSpacing: '-2px'
            }}
          >
            Start understanding your body.
          </h2>

          <p className="mt-4 text-base md:text-lg text-ston-text-secondary max-w-[560px] mx-auto">
            STON.FIT is live on iOS and Android. Download the app and start training smarter today.
          </p>

          <StoreBadges height={48} className="mt-10" />
            <p className="mt-4 text-sm text-ston-text-secondary">
              <span className="text-amber-400">★</span>{' '}
              <span className="font-semibold text-ston-text">4.9</span> on Google Play
            </p>

          <div className="mt-12 max-w-[480px] mx-auto">
            <p className="mb-4 text-sm text-ston-text-muted">
              Prefer email? Get product news and training tips.
            </p>
            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 bg-ston-card border border-ston-border rounded-full px-6 py-4 text-base text-ston-text placeholder:text-ston-text-muted focus:border-ston-purple transition-colors outline-none"
                  data-testid="waitlist-email-input"
                />
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="relative px-8 py-4 bg-ston-purple text-white rounded-full text-base font-semibold transition-all duration-300 hover:bg-ston-purple-light whitespace-nowrap overflow-hidden"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  data-testid="waitlist-submit-btn"
                >
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{ background: 'radial-gradient(circle at center, rgba(255,255,255,0.15) 0%, transparent 70%)' }}
                    animate={{ opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <span className="relative z-10">{isSubmitting ? 'Subscribing...' : 'Subscribe'}</span>
                </motion.button>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="text-center py-4"
                data-testid="waitlist-success"
              >
                <PartyPopper size={48} className="mx-auto text-ston-purple-light mb-4" />
                <h3 className="text-lg font-semibold text-ston-text">You're in!</h3>
                <p className="text-sm text-ston-text-secondary mt-2">
                  Thanks for subscribing — we'll keep you posted with news and tips.
                </p>
              </motion.div>
            )}

            {!isSubmitted && errorMessage && (
              <p className="mt-3 text-sm text-red-400" data-testid="waitlist-error">
                {errorMessage}
              </p>
            )}

            <p className="mt-4 text-sm text-ston-text-muted">
              Questions? <a href="mailto:founder@ston.fit" className="text-ston-purple-light hover:underline">founder@ston.fit</a>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WaitlistCTA;
